import { LegacyWCService, SessionSignedPayload } from './LegacyWCService';
import { debounceTime, Subscription, take } from 'rxjs';
import {
  ArnConnector,
  IWalletConnectConnectorPayload,
} from '@arianee/arn-types';
import { ethers } from 'ethers';

/**
 * Implement the abstract UI callbacks of the connector in a subclass of your own.
 */
export abstract class LegacyWCConnector implements ArnConnector {
  private sessionConnected: Subscription = Subscription.EMPTY;
  private sessionSigned: Subscription = Subscription.EMPTY;
  readonly legacyWcService: LegacyWCService;

  protected constructor(protected options: IWalletConnectConnectorPayload) {
    this.legacyWcService = new LegacyWCService(options.wcClientMeta);
  }

  async connect(): Promise<void> {
    return new Promise((resolve) => {
      if (this.sessionConnected) {
        this.sessionConnected.unsubscribe();
      }
      this.sessionConnected = this.legacyWcService.$isConnected
        .pipe(debounceTime(0))
        .subscribe((isConnected: boolean) => {
          if (isConnected) {
            resolve();
          }
        });
      this.connectDialog(this.options);
    });
  }

  async onConnectFromCache(): Promise<void> {
    // NOP
  }

  public async signMessage(message: string): Promise<string> {
    const isConnected = this.legacyWcService.$isConnected.getValue();
    if (!isConnected || !this.isConnectDialogVisible()) {
      await this.connect();
    }
    const signPromise = new Promise<string>((resolve) => {
      if (this.sessionSigned) {
        this.sessionSigned.unsubscribe();
      }
      this.sessionSigned = this.legacyWcService.sessionSigned
        .pipe(debounceTime(0), take(2))
        .subscribe((sigPayload: SessionSignedPayload) => {
          if (sigPayload.signature && sigPayload.signature != '') {
            const signature = sigPayload.signature;
            this.log(
              'Signed: { message: "%s", signature: "%s" }',
              message,
              signature
            );
            resolve(signature);
          }
        });

      this.requestSignature(message);
    });
    this.showSignMessage(signPromise);
    return signPromise;
  }

  signTypedData(
    _domain: ethers.TypedDataDomain,
    _types: Record<string, ethers.TypedDataField[]>,
    _value: Record<string, any>
  ): Promise<string> {
    throw new Error('Method not implemented');
  }

  signTransaction(
    _transaction: ethers.utils.Deferrable<ethers.providers.TransactionRequest>
  ): Promise<string> {
    throw new Error('Method not implemented');
  }

  sendTransaction(
    _transaction: ethers.utils.Deferrable<ethers.providers.TransactionRequest>
  ): Promise<ethers.providers.TransactionResponse> {
    throw new Error('Method not implemented');
  }

  sendUncheckedTransaction(
    _transaction: ethers.utils.Deferrable<ethers.providers.TransactionRequest>
  ): Promise<string> {
    throw new Error('Method not implemented');
  }

  getChainId(): Promise<number> {
    throw new Error('Method not implemented');
  }

  public async switchChain(chainId: number): Promise<void> {
    const isConnected = this.legacyWcService.$isConnected.getValue();
    if (!isConnected) {
      return;
    }
    // TODO: Check if the provider already know the chainId, if not use the wallet_addEthereumChain method to add it
    // https://docs.metamask.io/guide/rpc-api.html#wallet-addethereumchain
    await this.legacyWcService.requestSwitchChain(chainId);
  }

  public async disconnect(): Promise<void> {
    await this.legacyWcService.logout();
    this.log('Disconnected');
  }

  /**
   * Asks the user to connect.
   * @param data
   * @protected
   */
  protected abstract connectDialog(data: IWalletConnectConnectorPayload): void;

  protected abstract showSignMessage(until: Promise<string>): void;

  protected abstract isConnectDialogVisible(): boolean;

  protected abstract requestSignature(message: string): void;

  protected log(...data: any[]) {
    console.info(`[${this.constructor.name}]`, ...data);
  }
}
